/**
 * The Next steps cell: one item's actions, added, reworded, dated, assigned and deleted.
 *
 * **Why this is a panel and not a text box.** Until 20 September 2026 an item had one Next steps
 * cell, and typing in it was how an action came to exist. Joanne asked what to do when she has
 * marked one done and then wants to action the same publication for another client, and there
 * was no answer: reopening it loses the record that the first piece of work was finished, and
 * rewording it loses the same thing. An item can hold several actions now, so the cell holds a
 * summary of them and this is where they are edited.
 *
 * Three things here are deliberate.
 *
 * **Each action writes on its own.** There is no Save. A cell editor hands one value back to the
 * grid and the grid writes it in one PATCH; actions are separate records and adding one is not
 * the same act as rewording another. So each row calls the API when she leaves it, and the panel
 * reports what happened — which also means closing it never discards work.
 *
 * **The client is a picker of her own client names, plus "Everyone".** Not free text: the board
 * paper matches an action to a client by name, and a typo would be work that silently appears in
 * nobody's paper. Empty is a real choice and the default — an action need not be for anybody in
 * particular, and all 176 of her pre-existing next steps are exactly that.
 *
 * **The date and the status still come out of her own sentence.** `actionPatchFor` is shared with
 * the review queue and with the manual-add dialog, so writing "responses by 31 January" here
 * dates the action exactly as it would there. It is a matcher rather than a model call, and it
 * declines rather than guesses — see `nextstep.ts`.
 */

import { useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { actionPatchFor } from "../nextstep";
import type { Action } from "../types";

interface Props {
  itemId: string;
  itemTitle: string;
  /** The clients a new action may be given: everything not archived. */
  live: string[];
  /** Names already on the item that are not on offer — archived clients she tagged before. */
  alsoNamed?: string[];
  onClose: () => void;
  /** Called after any write, so the row behind the panel shows its new derived summary. */
  onChanged: () => void;
  onError: (error: unknown) => void;
}

/** What an empty client reads as. It is a value, not a missing one — see the module note. */
const EVERYONE = "Everyone on this item";

function formatDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

export function ActionsPanel({
  itemId,
  itemTitle,
  live,
  alsoNamed = [],
  onClose,
  onChanged,
  onError,
}: Props) {
  const [rows, setRows] = useState<Action[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");

  // A new action being composed. Kept apart from `rows` so that a half-typed one is never
  // mistaken for a record, and so cancelling it writes nothing.
  const [draft, setDraft] = useState("");
  const [draftClient, setDraftClient] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api
      .listItemActions(itemId)
      .then((page) => setRows(page.items))
      .catch(onError)
      .finally(() => setLoading(false));
  }, [itemId, onError]);

  useEffect(load, [load]);

  const changed = useCallback(() => {
    load();
    onChanged();
  }, [load, onChanged]);

  const add = async () => {
    const text = draft.trim();
    if (!text) return;
    setBusy("new");
    try {
      // The same rules the log cell and the review queue apply: the status follows the text, and
      // a deadline in her own sentence becomes the date. Shared so the three cannot drift.
      const implied = actionPatchFor(text, { next_steps_status: "", due_date: "" });
      await api.createAction(itemId, {
        next_steps: text,
        client: draftClient,
        next_steps_status: "open",
        ...(implied.due_date ? { due_date: implied.due_date } : {}),
      });
      setNote(
        implied.due_date
          ? `Added, due ${formatDate(implied.due_date)} — read from “${implied.read?.basis}”.`
          : "Added. Give it a date if it has one.",
      );
      setDraft("");
      setDraftClient("");
      changed();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  const update = async (action: Action, patch: Parameters<typeof api.patchAction>[2]) => {
    setBusy(action.id);
    try {
      await api.patchAction(itemId, action.id, patch);
      setNote("");
      changed();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  /**
   * Rewording reopens a completed action, deliberately — the rule `statusForNextStep` states and
   * the log cell has always followed. The alternative is a step she has just written appearing
   * on no screen at all.
   */
  const reword = async (action: Action, text: string) => {
    const wanted = text.trim();
    if (!wanted || wanted === action.next_steps) return;
    const patch: Parameters<typeof api.patchAction>[2] = { next_steps: wanted };
    if (action.next_steps_status === "complete") patch.next_steps_status = "open";
    if (!action.due_date) {
      const implied = actionPatchFor(wanted, { next_steps_status: "open", due_date: "" });
      if (implied.due_date) patch.due_date = implied.due_date;
    }
    await update(action, patch);
  };

  const remove = async (action: Action) => {
    setBusy(action.id);
    try {
      await api.deleteAction(itemId, action.id);
      setNote("Deleted.");
      changed();
    } catch (error) {
      onError(error);
    } finally {
      setBusy(null);
    }
  };

  const clients = [...live, ...alsoNamed.filter((name) => !live.includes(name))];

  return (
    <div className="actions-panel-backdrop" role="dialog" aria-label="Actions for this item">
      <div className="actions-panel">
        <div className="panel-head">
          <h3>Next steps</h3>
          <p className="small muted">{itemTitle}</p>
        </div>

        {loading ? (
          <p className="small muted">Loading…</p>
        ) : (
          <div className="action-rows">
            {rows.length === 0 && (
              <p className="small muted">
                Nothing yet. Anything you add here appears on the Actions screen.
              </p>
            )}

            {rows.map((action) => (
              <div
                key={action.id}
                className={action.next_steps_status === "complete" ? "action-row done" : "action-row"}
              >
                <select
                  value={action.client}
                  disabled={busy === action.id}
                  onChange={(e) => update(action, { client: e.target.value })}
                  aria-label="Whose work this is"
                >
                  <option value="">{EVERYONE}</option>
                  {clients.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                  {/* A client who has left, still named on this action: shown so that changing
                      something else on the row cannot silently reassign her history. */}
                  {action.client && !clients.includes(action.client) && (
                    <option value={action.client}>{action.client} (archived)</option>
                  )}
                </select>

                <textarea
                  defaultValue={action.next_steps}
                  rows={2}
                  disabled={busy === action.id}
                  onBlur={(e) => reword(action, e.target.value)}
                  aria-label="What to do"
                />

                <input
                  type="date"
                  value={action.due_date || ""}
                  disabled={busy === action.id}
                  onChange={(e) => update(action, { due_date: e.target.value })}
                  aria-label="When it is due"
                />

                <button
                  type="button"
                  disabled={busy === action.id}
                  onClick={() =>
                    update(action, {
                      next_steps_status:
                        action.next_steps_status === "complete" ? "open" : "complete",
                    })
                  }
                >
                  {action.next_steps_status === "complete" ? "Reopen" : "Done"}
                </button>

                {/* Confirmed in the row, like the delete on the Reports screen: a browser dialog
                    cannot name what it is about to remove and arrives over what she is reading. */}
                <ConfirmDelete
                  disabled={busy === action.id}
                  onConfirm={() => remove(action)}
                  label={action.next_steps}
                />
              </div>
            ))}
          </div>
        )}

        <div className="action-new">
          <select
            value={draftClient}
            onChange={(e) => setDraftClient(e.target.value)}
            aria-label="Whose work this is"
          >
            <option value="">{EVERYONE}</option>
            {clients.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <textarea
            value={draft}
            rows={2}
            placeholder="What needs doing, and by when"
            onChange={(e) => setDraft(e.target.value)}
            aria-label="A new next step"
          />
          <button type="button" disabled={!draft.trim() || busy === "new"} onClick={add}>
            Add
          </button>
        </div>

        {note && <p className="small">{note}</p>}

        <div className="panel-actions">
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

/** A red cross that asks once, in the row. Mirrors the Reports screen's delete. */
function ConfirmDelete({
  disabled,
  onConfirm,
  label,
}: {
  disabled: boolean;
  onConfirm: () => void;
  label: string;
}) {
  const [asking, setAsking] = useState(false);
  if (!asking) {
    return (
      <button
        type="button"
        className="link danger"
        disabled={disabled}
        title={`Delete “${label.slice(0, 60)}”`}
        onClick={() => setAsking(true)}
      >
        ✕
      </button>
    );
  }
  return (
    <span className="confirm-delete">
      <button type="button" className="link danger" disabled={disabled} onClick={onConfirm}>
        Delete
      </button>
      <button type="button" className="link" onClick={() => setAsking(false)}>
        Keep
      </button>
    </span>
  );
}
